import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api/client';
import type { ApiResponse, IdentityBoard } from '@/api/types';
import Layout from '@/components/Layout';

// ─── Public ───────────────────────────────────────────────────────
import Landing from '@/pages/Landing';
import Login from '@/pages/auth/Login';
import Verify from '@/pages/auth/Verify';

// ─── Dashboard (Phase 1E.1) ───────────────────────────────────────
import Dashboard from '@/pages/Dashboard';

// ─── Pre-Lab (onboarding chain) ───────────────────────────────────
import Welcome from '@/pages/onboarding/Welcome';
import Onboarding from '@/pages/onboarding/Onboarding';
import Profile from '@/pages/Profile';
import ExtendedProfileLab from '@/pages/onboarding/ExtendedProfile';
import HomeLab from '@/pages/home/Index';
import GamificationLab from '@/pages/gamification/Index';

// ─── Identity Board feature-lab ───────────────────────────────────
import BoardCreate from '@/pages/board/Create';
import BoardOverview from '@/pages/board/Overview';
import BoardSignalsLab from '@/pages/board/Signals';
import Matches from '@/pages/discovery/Matches';
import MatchDetail from '@/pages/discovery/MatchDetail';
import Waves from '@/pages/discovery/Waves';

// ─── Nostalgia feature-lab ────────────────────────────────────────
import NostalgiaDashboard from '@/pages/nostalgia/NostalgiaDashboard';
import BoardsList from '@/pages/nostalgia/BoardsList';
import BoardDetail from '@/pages/nostalgia/BoardDetail';
import LibraryList from '@/pages/nostalgia/LibraryList';
import StickersList from '@/pages/nostalgia/StickersList';
import TemplatesList from '@/pages/nostalgia/TemplatesList';
import MemoriesLab from '@/pages/nostalgia/Memories';

// ─── Phase 1E.1 Commit 3 — Admin ───────────────────────────────────
import AdminRoute from '@/components/AdminRoute';
import AdminDashboard from '@/pages/admin/Dashboard';
import VerificationQueueLab from '@/pages/admin/VerificationQueue';
import ProviderVerificationsLab from '@/pages/admin/ProviderVerifications';
import ClaimsLab from '@/pages/admin/Claims';
import InterestsQueueLab from '@/pages/admin/InterestsQueue';
import AnalyticsLab from '@/pages/admin/Analytics';
import YashFraudLab from '@/pages/admin/YashFraud';
import DharoharModerationLab from '@/pages/admin/DharoharModeration';
import AdminReportsLab from '@/pages/admin/Reports';
import AdminRecruitmentLab from '@/pages/admin/Recruitment';
import AnnouncementsLab from '@/pages/announcements/Index';
import AdminAnnouncementsLab from '@/pages/admin/Announcements';
import AdminWinsLab from '@/pages/admin/Wins';

// ─── Phase 1E.1 Commit 2 — feature-labs for the live backend ──────
import InterestsLab from '@/pages/interests/Index';
import MyProposals from '@/pages/interests/MyProposals';
import NotificationsLab from '@/pages/notifications/Index';
import EventOrganizersLab from '@/pages/event-organizers/Index';
import OrganizerDirectory from '@/pages/event-organizers/Directory';
import OrganizerDirectoryDetail from '@/pages/event-organizers/DirectoryDetail';
import UtsavLab from '@/pages/utsav/Index';
import VolunteersLab from '@/pages/volunteers/Index';
import InstitutionsLab from '@/pages/institutions/Index';
import InstitutionDetail from '@/pages/institutions/Detail';
import InstitutionManageLab from '@/pages/institutions/Manage';
import InstitutionAdminLab from '@/pages/institutions/Admin';
import CreateInstitution from '@/pages/institutions/Create';
import FollowedInstitutions from '@/pages/institutions/Followed';
import WorshipEventsLab from '@/pages/worship-events/Index';
import EventsLab from '@/pages/events/Index';
import BazaarLab from '@/pages/bazaar/Index';
import SearchLab from '@/pages/search/Index';
import EventDraftLab from '@/pages/events/Draft';
import EventSeatsLab from '@/pages/events/Seats';
import EventDeskLab from '@/pages/events/Desk';
import EventThreadLab from '@/pages/events/Thread';
import EventCheckoutLab from '@/pages/events/Checkout';
import OrganizerLab from '@/pages/events/Organizer';
import MyTicketsLab from '@/pages/events/MyTickets';
import HouseholdsLab from '@/pages/households/Index';
import CirclesLab from '@/pages/circles/Index';
import WaitlistLab from '@/pages/admin/Waitlist';
import ProviderMessagesLab from '@/pages/providers/Messages';
import PublicSurfaceLab from '@/pages/public/Index';
import ProvidersLab from '@/pages/providers/Index';
import ProviderDetail from '@/pages/providers/Detail';
import ProviderManage from '@/pages/providers/Manage';
import ProviderDeskLab from '@/pages/providers/Desk';
import TelemetryLab from '@/pages/admin/Telemetry';
import QuestionsLab from '@/pages/questions/Index';
import QuestionDetail from '@/pages/questions/Detail';
import ComposerLab from '@/pages/composer/Compose';
import ComposerFeedLab from '@/pages/composer/Feed';
import ComposerInsightsLab from '@/pages/composer/Insights';
import VoicesLab from '@/pages/voices/Index';
import VoiceDetail from '@/pages/voices/Detail';
import ConnectionsLab from '@/pages/connections/Index';
import YashLab from '@/pages/yash/Index';
import DharoharLab from '@/pages/dharohar/Index';
import SettingsLab from '@/pages/settings/Index';
import TestMemberLab from '@/pages/dev/TestMember';
import ConversationsLab from '@/pages/conversations/Messages';

// ═══════════════════════════════════════════════════════════════════
// Route guards
// ═══════════════════════════════════════════════════════════════════

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Discovery routes require board completion. If the user hasn't
// completed 3+ blocks yet (`discoveryUnlocked === false`), send them
// to the board overview where the completeness progress is shown.
function DiscoveryRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data, isLoading: boardLoading } = useQuery({
    queryKey: ['identity-board'],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>('/identity-board'),
    enabled: isAuthenticated,
    retry: false,
  });

  if (authLoading || (isAuthenticated && boardLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const board = data?.data;
  if (!board || !board.discoveryUnlocked) {
    return <Navigate to="/lab/identity-board" replace />;
  }

  return <>{children}</>;
}

// `/` branches on auth state — public Landing for guests, unified
// Dashboard for signed-in users.
function Home() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Dashboard /> : <Landing />;
}

// ═══════════════════════════════════════════════════════════════════
// App
//
// Route structure (Phase 1E.1):
//   /                            Home — Landing (guests) / Dashboard (auth)
//   /login, /auth/verify         Public auth
//   /welcome, /onboarding        Pre-Lab onboarding chain
//   /profile                     User profile (global)
//
//   /lab/identity-board/*        Identity Board feature-lab
//     ├── /                      Board overview
//     ├── /create                Board builder wizard
//     ├── /discovery             Match list (gated on board completion)
//     ├── /discovery/:userId     Match detail
//     └── /waves                 Wave management
//
//   /lab/nostalgia/*             Nostalgia feature-lab (now in Layout)
//     ├── /                      Nostalgia dashboard
//     ├── /boards, /boards/:id   Memory boards CRUD
//     ├── /library               Curated catalog
//     ├── /stickers              Sticker library
//     └── /templates             Layout templates
//
// Legacy shims (these return Navigate replace, so bookmarks still
// land on the right page):
//   /board, /board/create        → /lab/identity-board, /lab/identity-board/create
//   /discovery, /discovery/:userId, /waves
//                                → /lab/identity-board/discovery, ...
//   /nostalgia/*                 → /lab/nostalgia/*
// ═══════════════════════════════════════════════════════════════════

export default function App() {
  return (
    <Routes>
      {/* ─── Public ─────────────────────────────────────────────── */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/verify" element={<Verify />} />

      {/* ─── Pre-Lab + Lab — all under the unified Layout shell ─── */}
      <Route element={<Layout />}>
        {/* Pre-Lab (onboarding chain + global profile) */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <Welcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/extended"
          element={
            <ProtectedRoute>
              <ExtendedProfileLab />
            </ProtectedRoute>
          }
        />

        {/* Identity Board feature-lab */}
        <Route
          path="/lab/identity-board"
          element={
            <ProtectedRoute>
              <BoardOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/identity-board/signals"
          element={
            <ProtectedRoute>
              <BoardSignalsLab />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/identity-board/create"
          element={
            <ProtectedRoute>
              <BoardCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/identity-board/discovery"
          element={
            <DiscoveryRoute>
              <Matches />
            </DiscoveryRoute>
          }
        />
        <Route
          path="/lab/identity-board/discovery/:userId"
          element={
            <DiscoveryRoute>
              <MatchDetail />
            </DiscoveryRoute>
          }
        />
        <Route
          path="/lab/identity-board/waves"
          element={
            <DiscoveryRoute>
              <Waves />
            </DiscoveryRoute>
          }
        />

        {/* Nostalgia feature-lab (rewrapped inside Layout per 1E.1) */}
        <Route
          path="/lab/nostalgia"
          element={
            <ProtectedRoute>
              <NostalgiaDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/lab/nostalgia/memories" element={<MemoriesLab />} />
        <Route
          path="/lab/nostalgia/boards"
          element={
            <ProtectedRoute>
              <BoardsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/nostalgia/boards/:id"
          element={
            <ProtectedRoute>
              <BoardDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/nostalgia/library"
          element={
            <ProtectedRoute>
              <LibraryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/nostalgia/stickers"
          element={
            <ProtectedRoute>
              <StickersList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/nostalgia/templates"
          element={
            <ProtectedRoute>
              <TemplatesList />
            </ProtectedRoute>
          }
        />

        {/* ─── Phase 1E.1 Commit 2 — new feature-labs ────────── */}

        {/* Custom Interests (1A) */}
        <Route path="/lab/interests" element={<ProtectedRoute><InterestsLab /></ProtectedRoute>} />
        <Route path="/lab/interests/my-proposals" element={<ProtectedRoute><MyProposals /></ProtectedRoute>} />

        {/* Notifications (1D.1) — list + lifecycle + funnel tester */}
        <Route path="/lab/notifications" element={<ProtectedRoute><NotificationsLab /></ProtectedRoute>} />

        {/* Event Organizers (1C.1) */}
        <Route path="/lab/event-organizers" element={<ProtectedRoute><EventOrganizersLab /></ProtectedRoute>} />
        <Route path="/lab/event-organizers/directory" element={<ProtectedRoute><OrganizerDirectory /></ProtectedRoute>} />
        <Route path="/lab/event-organizers/directory/:id" element={<ProtectedRoute><OrganizerDirectoryDetail /></ProtectedRoute>} />

        {/* Utsav festival calendar (1B.4) */}
        <Route path="/lab/utsav" element={<ProtectedRoute><UtsavLab /></ProtectedRoute>} />

        {/* Volunteers (1B.6) — caller's history */}
        <Route path="/lab/volunteers" element={<ProtectedRoute><VolunteersLab /></ProtectedRoute>} />

        {/* Institutions (1B.2/3/8) */}
        <Route path="/lab/institutions" element={<ProtectedRoute><InstitutionsLab /></ProtectedRoute>} />
        <Route path="/lab/institutions/new" element={<ProtectedRoute><CreateInstitution /></ProtectedRoute>} />
        <Route path="/lab/institutions/followed" element={<ProtectedRoute><FollowedInstitutions /></ProtectedRoute>} />
        <Route path="/lab/institutions/admin" element={<ProtectedRoute><InstitutionAdminLab /></ProtectedRoute>} />
        <Route path="/lab/institutions/:id/manage" element={<ProtectedRoute><InstitutionManageLab /></ProtectedRoute>} />
        <Route path="/lab/institutions/:idOrSlug" element={<ProtectedRoute><InstitutionDetail /></ProtectedRoute>} />

        {/* Worship Events (1B.5) — events filtered to realm=worship */}
        <Route path="/lab/worship-events" element={<ProtectedRoute><WorshipEventsLab /></ProtectedRoute>} />

        {/* Home feed + gamification (the assembled spine + reward loop) */}
        <Route path="/lab/home" element={<ProtectedRoute><HomeLab /></ProtectedRoute>} />
        <Route path="/lab/gamification" element={<ProtectedRoute><GamificationLab /></ProtectedRoute>} />

        {/* Events (general community) */}
        <Route path="/lab/events" element={<ProtectedRoute><EventsLab /></ProtectedRoute>} />
        <Route path="/lab/events/organizer" element={<ProtectedRoute><OrganizerLab /></ProtectedRoute>} />
        <Route path="/lab/events/draft" element={<ProtectedRoute><EventDraftLab /></ProtectedRoute>} />
        <Route path="/lab/events/seats" element={<ProtectedRoute><EventSeatsLab /></ProtectedRoute>} />
        <Route path="/lab/events/desk" element={<ProtectedRoute><EventDeskLab /></ProtectedRoute>} />
        <Route path="/lab/bazaar" element={<ProtectedRoute><BazaarLab /></ProtectedRoute>} />
        <Route path="/lab/search" element={<ProtectedRoute><SearchLab /></ProtectedRoute>} />
        <Route path="/lab/tickets" element={<ProtectedRoute><MyTicketsLab /></ProtectedRoute>} />
        <Route path="/lab/events/:eventId/thread" element={<ProtectedRoute><EventThreadLab /></ProtectedRoute>} />
        <Route path="/lab/events/:eventId/checkout" element={<ProtectedRoute><EventCheckoutLab /></ProtectedRoute>} />
        <Route path="/lab/households" element={<ProtectedRoute><HouseholdsLab /></ProtectedRoute>} />
        <Route path="/lab/circles" element={<ProtectedRoute><CirclesLab /></ProtectedRoute>} />
        <Route path="/lab/waitlist" element={<ProtectedRoute><WaitlistLab /></ProtectedRoute>} />
        <Route path="/lab/provider-messages" element={<ProtectedRoute><ProviderMessagesLab /></ProtectedRoute>} />
        <Route path="/lab/conversations" element={<ProtectedRoute><ConversationsLab /></ProtectedRoute>} />
        <Route path="/lab/public" element={<ProtectedRoute><PublicSurfaceLab /></ProtectedRoute>} />
        {/* Not behind ProtectedRoute: the dev key is the gate, and you may want
            to become the test member before you have any Lab session. */}
        <Route path="/lab/test-member" element={<TestMemberLab />} />

        {/* Providers — service directory */}
        <Route path="/lab/providers/desk" element={<ProtectedRoute><ProviderDeskLab /></ProtectedRoute>} />
        <Route path="/lab/telemetry" element={<ProtectedRoute><TelemetryLab /></ProtectedRoute>} />
        <Route path="/lab/providers" element={<ProtectedRoute><ProvidersLab /></ProtectedRoute>} />
        <Route path="/lab/providers/new" element={<ProtectedRoute><ProviderManage /></ProtectedRoute>} />
        <Route path="/lab/providers/:idOrSlug/edit" element={<ProtectedRoute><ProviderManage /></ProtectedRoute>} />
        <Route path="/lab/providers/:idOrSlug" element={<ProtectedRoute><ProviderDetail /></ProtectedRoute>} />

        {/* Curios — community Q&A */}
        <Route path="/lab/questions" element={<ProtectedRoute><QuestionsLab /></ProtectedRoute>} />
        <Route path="/lab/questions/:id" element={<ProtectedRoute><QuestionDetail /></ProtectedRoute>} />

        {/* Announcements — the what's-happening layer, targeting shown */}
        <Route path="/lab/announcements" element={<ProtectedRoute><AnnouncementsLab /></ProtectedRoute>} />

        {/* Composer — the one box, its feed, and the OP-only receipt */}
        <Route path="/lab/composer" element={<ProtectedRoute><ComposerLab /></ProtectedRoute>} />
        <Route path="/lab/composer/feed" element={<ProtectedRoute><ComposerFeedLab /></ProtectedRoute>} />
        <Route path="/lab/composer/insights/:type/:id" element={<ProtectedRoute><ComposerInsightsLab /></ProtectedRoute>} />

        {/* Voices (Awaaz) */}
        <Route path="/lab/voices" element={<ProtectedRoute><VoicesLab /></ProtectedRoute>} />
        <Route path="/lab/voices/:idOrSlug" element={<ProtectedRoute><VoiceDetail /></ProtectedRoute>} />

        {/* Connections — follow graph */}
        <Route path="/lab/connections" element={<ProtectedRoute><ConnectionsLab /></ProtectedRoute>} />

        {/* Yash — rewards */}
        <Route path="/lab/yash" element={<ProtectedRoute><YashLab /></ProtectedRoute>} />

        {/* Dharohar — heritage */}
        <Route path="/lab/dharohar" element={<ProtectedRoute><DharoharLab /></ProtectedRoute>} />

        {/* Settings & Privacy */}
        <Route path="/lab/settings" element={<ProtectedRoute><SettingsLab /></ProtectedRoute>} />

        {/* ─── Phase 1E.1 Commit 3 — Admin sub-dashboard ────── */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/verification-queue" element={<AdminRoute><VerificationQueueLab /></AdminRoute>} />
        <Route path="/admin/provider-verifications" element={<AdminRoute><ProviderVerificationsLab /></AdminRoute>} />
        <Route path="/admin/claims" element={<AdminRoute><ClaimsLab /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><AdminReportsLab /></AdminRoute>} />
        <Route path="/admin/recruitment" element={<AdminRoute><AdminRecruitmentLab /></AdminRoute>} />
        <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncementsLab /></AdminRoute>} />
        <Route path="/admin/wins" element={<AdminRoute><AdminWinsLab /></AdminRoute>} />
        <Route path="/admin/interests-queue" element={<AdminRoute><InterestsQueueLab /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><AnalyticsLab /></AdminRoute>} />
        <Route path="/admin/yash-fraud" element={<AdminRoute><YashFraudLab /></AdminRoute>} />
        <Route path="/admin/dharohar-moderation" element={<AdminRoute><DharoharModerationLab /></AdminRoute>} />
      </Route>

      {/* ─── Legacy redirect shims ──────────────────────────────── */}
      {/* Phase 1E.1 moved every feature under /lab/<feature>/*. Keep
          these Navigate shims so old bookmarks + the magic-link
          redirect chain still land on the right page. */}
      <Route path="/board" element={<Navigate to="/lab/identity-board" replace />} />
      <Route
        path="/board/create"
        element={<Navigate to="/lab/identity-board/create" replace />}
      />
      <Route
        path="/discovery"
        element={<Navigate to="/lab/identity-board/discovery" replace />}
      />
      <Route
        path="/discovery/:userId"
        element={<NavigateWithParams to="/lab/identity-board/discovery" param="userId" />}
      />
      <Route
        path="/waves"
        element={<Navigate to="/lab/identity-board/waves" replace />}
      />
      <Route path="/nostalgia" element={<Navigate to="/lab/nostalgia" replace />} />
      <Route
        path="/nostalgia/boards"
        element={<Navigate to="/lab/nostalgia/boards" replace />}
      />
      <Route
        path="/nostalgia/boards/:id"
        element={<NavigateWithParams to="/lab/nostalgia/boards" param="id" />}
      />
      <Route
        path="/nostalgia/library"
        element={<Navigate to="/lab/nostalgia/library" replace />}
      />
      <Route
        path="/nostalgia/stickers"
        element={<Navigate to="/lab/nostalgia/stickers" replace />}
      />
      <Route
        path="/nostalgia/templates"
        element={<Navigate to="/lab/nostalgia/templates" replace />}
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Tiny helper for redirect shims that need to preserve a path param.
import { useParams } from 'react-router-dom';
function NavigateWithParams({ to, param }: { to: string; param: string }) {
  const params = useParams();
  const value = params[param];
  return <Navigate to={value ? `${to}/${value}` : to} replace />;
}
